import React from "react";

/**
 * A site's identity: its initials on a muted, desaturated tone.
 *
 * Red, amber and green mean error, waiting and live everywhere on this screen, so a site can never
 * be one of them. These tones are deliberately dull (slate, plum, teal-grey…) and the initials do
 * the real work, which keeps sites distinguishable well past the eighth one.
 */
const TONES = [
  "#4f6173", // slate
  "#6b4a68", // plum
  "#416d6c", // teal-grey
  "#75685b", // taupe
  "#55577a", // indigo-grey
  "#7a5a76", // mauve
  "#5d6b78", // steel
  "#6a5649", // umber
] as const;

export function siteTone(siteId: string): string {
  let hash = 0;
  for (let index = 0; index < siteId.length; index += 1) hash = (hash * 31 + siteId.charCodeAt(index)) >>> 0;
  return TONES[hash % TONES.length] ?? TONES[0];
}

/** "Maison Duval" → "MD", "Atelier" → "AT". */
export function siteInitials(name: string): string {
  const words = name.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (words.length === 0) return "··";
  if (words.length === 1) return (words[0] ?? "").slice(0, 2).toUpperCase();
  return `${(words[0] ?? "").charAt(0)}${(words[1] ?? "").charAt(0)}`.toUpperCase();
}

export function SiteMark({ siteId, name, size = "sm" }: { siteId: string; name: string; size?: "sm" | "md" }) {
  return (
    <span className={`mc-mark size-${size}`} style={{ background: siteTone(siteId) }} aria-hidden="true">
      {siteInitials(name)}
    </span>
  );
}
